import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";
import AboutBuilder from "../components/common/aboutBuilder";

import INFO from "../data/user";
import { listArtifacts, TOOL_TOPICS } from "../components/articles/artifacts";

import "./styles/tools.css";

// Real terms that return hits, shown under the tools search box.
const SUGGESTED = [
	"NIST",
	"deploy",
	"security",
	"accountability",
	"token cost",
	"readiness",
];

const Tools = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const artifacts = listArtifacts();

	const [topic, setTopic] = useState("All");
	const [query, setQuery] = useState("");

	const q = query.trim().toLowerCase();
	const filtered = artifacts.filter((a) => {
		if (topic !== "All" && a.topic !== topic) return false;
		if (!q) return true;
		return `${a.title} ${a.blurb}`.toLowerCase().includes(q);
	});

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Tools | ${INFO.main.title}`}</title>
				<meta
					name="description"
					content="Free interactive AI governance and security tools: an AI readiness scorecard, a deployment risk check, a review-role template, a token cost calculator, and an agent security checklist."
				/>
			</Helmet>

			<div className="page-content">
				<NavBar active="tools" />
				<div className="content-wrapper">
					<div className="tools-logo-container">
						<div className="tools-logo">
							<Logo width={46} />
						</div>
					</div>

					<div className="tools-main-container">
						<div className="title tools-title">
							Interactive Tools
						</div>
						<div className="subtitle tools-subtitle">
							Working tools that turn the ideas in these articles
							into something you can use in five minutes. Free, and
							no signup to run one.
						</div>

						<div className="tools-filter">
								{["All", ...TOOL_TOPICS].map((t) => (
									<button
										key={t}
										className={`tools-chip${
											topic === t ? " active" : ""
										}`}
										onClick={() => setTopic(t)}
									>
										{t}
									</button>
								))}
							</div>

							<div className="tools-search">
								<input
									type="text"
									placeholder="Search tools..."
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									aria-label="Search tools"
								/>
								<div className="tools-suggest">
									<span className="tools-suggest-label">
										Try:
									</span>
									{SUGGESTED.map((k) => (
										<button
											key={k}
											className="tools-suggest-term"
											onClick={() => setQuery(k)}
										>
											{k}
										</button>
									))}
								</div>
							</div>

							<div className="tools-grid">
							{filtered.length === 0 && (
									<div className="tools-empty">
										No tools match that filter yet.
									</div>
								)}
								{filtered.map((a) => (
									a.externalUrl ? (
									<a
										className="tools-card"
										href={a.externalUrl}
										target="_blank"
										rel="noopener noreferrer"
										key={a.key}
									>
										<div className="tools-card-title">
											{a.title}
										</div>
										<div className="tools-card-blurb">
											{a.blurb}
										</div>
										<div className="tools-card-link">
											Open tool ↗
										</div>
									</a>
									) : (
								<Link
									className="tools-card"
									to={`/tool/${a.key}`}
									key={a.key}
								>
									<div className="tools-card-title">
										{a.title}
									</div>
									<div className="tools-card-blurb">
										{a.blurb}
									</div>
									<div className="tools-card-link">
										Open tool →
									</div>
								</Link>
							)))}
						</div>

						<div className="tools-path-callout">
							New to this work? Start with the guided path:{" "}
							<Link to="/path">
								AI Governance for FinServ Leaders →
							</Link>
						</div>

						<AboutBuilder context="Most of these tools started as a question I wanted answered, then turned into something you can use in five minutes." />
					</div>
					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Tools;
